import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wine, LogOut, CalendarDays, Users, Trash2, Loader2, Phone, Mail, Search, BadgeCheck, Utensils, Send, CalendarX2, Plus, X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-[#57534E] uppercase tracking-wider">{label}</p>
        <div className={`h-9 w-9 rounded-full flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="mt-3 font-serif text-4xl text-[#1C1917]">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [tableBookings, setTableBookings] = useState([]);
  const [stats, setStats] = useState({ total_bookings: 0, wine_club_signups: 0, total_guests: 0, table_bookings: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("tastings");
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockDate, setNewBlockDate] = useState("");
  const [blockBusy, setBlockBusy] = useState(false);

  const load = async () => {
    try {
      const [b, s, t, bd] = await Promise.all([
        api.get("/bookings"),
        api.get("/admin/stats"),
        api.get("/table-bookings"),
        api.get("/wine-bar/blocked-dates"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setTableBookings(t.data);
      setBlockedDates(bd.data.dates || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking removed.");
      load();
    } catch (err) {
      toast.error("Could not delete booking.");
    }
  };

  const handleDeleteTable = async (id) => {
    try {
      await api.delete(`/table-bookings/${id}`);
      toast.success("Table booking removed.");
      load();
    } catch (err) {
      toast.error("Could not delete table booking.");
    }
  };

  const [resendingId, setResendingId] = useState(null);

  const handleResend = async (kind, id) => {
    setResendingId(id);
    try {
      const path = kind === "table" ? `/table-bookings/${id}/resend-confirmation` : `/bookings/${id}/resend-confirmation`;
      const res = await api.post(path);
      toast.success(`Confirmation resent to ${res.data.email}`);
    } catch (err) {
      toast.error("Could not resend confirmation email.");
    } finally {
      setResendingId(null);
    }
  };

  const handleAddBlock = async () => {
    if (!newBlockDate) return;
    setBlockBusy(true);
    try {
      const res = await api.post("/admin/wine-bar/blocked-dates", { date: newBlockDate });
      setBlockedDates(res.data.dates);
      setNewBlockDate("");
      toast.success("Date closed for the wine bar.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not close this date.");
    } finally {
      setBlockBusy(false);
    }
  };

  const handleRemoveBlock = async (d) => {
    try {
      const res = await api.delete(`/admin/wine-bar/blocked-dates/${d}`);
      setBlockedDates(res.data.dates);
      toast.success("Date reopened for bookings.");
    } catch (err) {
      toast.error("Could not reopen this date.");
    }
  };

  const filtered = bookings.filter((b) => {
    const q = query.toLowerCase();
    return (
      b.name?.toLowerCase().includes(q) ||
      b.email?.toLowerCase().includes(q) ||
      b.phone?.toLowerCase().includes(q)
    );
  });

  const filteredTables = tableBookings.filter((b) => {
    const q = query.toLowerCase();
    return (
      b.name?.toLowerCase().includes(q) ||
      b.email?.toLowerCase().includes(q) ||
      b.phone?.toLowerCase().includes(q)
    );
  });

  const fmt = (d) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

  return (
    <div className="min-h-screen bg-[#F5F5F4]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#4A5D23]/10 flex items-center justify-center">
              <Wine className="h-5 w-5 text-[#4A5D23]" />
            </div>
            <div>
              <h1 className="font-serif text-xl text-[#1C1917] leading-tight">Bookings Dashboard</h1>
              <p className="text-xs text-[#57534E]">Gledswood Cellar Door</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E7E5E4] text-sm font-medium text-[#57534E] hover:bg-[#FDFBF7] transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-8">
          <StatCard icon={CalendarDays} label="Tastings" value={stats.total_bookings} accent="bg-[#4A5D23]/10 text-[#4A5D23]" />
          <StatCard icon={Users} label="Total Guests" value={stats.total_guests} accent="bg-[#4A5D23]/10 text-[#4A5D23]" />
          <StatCard icon={BadgeCheck} label="Wine Club" value={stats.wine_club_signups} accent="bg-[#722F37]/10 text-[#722F37]" />
          <StatCard icon={Utensils} label="Friday Wine Bar" value={stats.table_bookings} accent="bg-[#722F37]/10 text-[#722F37]" />
        </div>

        <div className="flex gap-2 mb-6">
          <button
            data-testid="tab-tastings"
            onClick={() => setTab("tastings")}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === "tastings" ? "bg-[#4A5D23] text-white" : "bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#4A5D23]"}`}
          >
            Wine Tastings
          </button>
          <button
            data-testid="tab-tables"
            onClick={() => setTab("tables")}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === "tables" ? "bg-[#722F37] text-white" : "bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#722F37]"}`}
          >
            Friday Wine Bar
          </button>
        </div>

        {tab === "tables" && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-6 mb-6" data-testid="closures-card">
            <div className="flex items-center gap-2 mb-1">
              <CalendarX2 className="h-5 w-5 text-[#722F37]" />
              <h2 className="font-serif text-xl text-[#1C1917]">Wine Bar Closures</h2>
            </div>
            <p className="text-sm text-[#57534E] mb-4">
              Close specific Fridays so guests can't book the wine bar on those nights. Changes apply instantly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end mb-5">
              <div>
                <label className="block text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Close a Friday</label>
                <input
                  type="date"
                  data-testid="closure-date-input"
                  value={newBlockDate}
                  onChange={(e) => setNewBlockDate(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#722F37]/20 focus:border-[#722F37] outline-none text-sm"
                />
              </div>
              <button
                onClick={handleAddBlock}
                disabled={blockBusy || !newBlockDate}
                data-testid="add-closure-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#722F37] text-white font-semibold text-sm hover:bg-[#5E262D] transition-colors disabled:opacity-50"
              >
                {blockBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Close date
              </button>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-2">Currently closed</p>
              {blockedDates.length === 0 ? (
                <p className="text-sm text-[#57534E]" data-testid="no-closures">No closures — the wine bar is open every Friday.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="closures-list">
                  {blockedDates.map((d) => (
                    <span key={d} data-testid={`closure-${d}`} className="inline-flex items-center gap-2 rounded-full bg-[#722F37]/8 border border-[#722F37]/20 text-[#722F37] pl-3 pr-1.5 py-1.5 text-sm font-medium">
                      {fmt(d)}
                      <button
                        onClick={() => handleRemoveBlock(d)}
                        data-testid={`remove-closure-${d}`}
                        className="h-5 w-5 rounded-full flex items-center justify-center hover:bg-[#722F37] hover:text-white transition-colors"
                        title="Reopen this date"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E7E5E4] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-[#1C1917]">{tab === "tastings" ? "Wine Tasting Bookings" : "Friday Night Wine Bar Bookings"}</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#57534E]" />
              <input
                data-testid="search-bookings"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, phone"
                className="pl-9 pr-4 py-2 rounded-full border border-[#E7E5E4] text-sm w-full sm:w-64 focus:ring-2 focus:ring-[#4A5D23]/20 focus:border-[#4A5D23] outline-none"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#4A5D23]" />
            </div>
          ) : tab === "tastings" ? (
            filtered.length === 0 ? (
              <div className="py-20 text-center text-[#57534E]" data-testid="no-bookings">No bookings yet.</div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="bg-[#FDFBF7] text-left text-[#57534E]">
                    <th className="px-6 py-3 font-semibold">Guest</th>
                    <th className="px-6 py-3 font-semibold">Contact</th>
                    <th className="px-6 py-3 font-semibold">Date</th>
                    <th className="px-6 py-3 font-semibold">Time</th>
                    <th className="px-6 py-3 font-semibold">Status</th>
                    <th className="px-6 py-3 font-semibold text-center">Guests</th>
                    <th className="px-6 py-3 font-semibold text-center">Boards</th>
                    <th className="px-6 py-3 font-semibold text-center">Club</th>
                    <th className="px-6 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b._id} className="border-t border-[#E7E5E4] hover:bg-[#FDFBF7]/60 transition-colors" data-testid={`booking-row-${b._id}`}>
                      <td className="px-6 py-4 font-medium text-[#1C1917]">{b.name}</td>
                      <td className="px-6 py-4 text-[#57534E]">
                        <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{b.email}</div>
                        <div className="flex items-center gap-1.5 mt-1"><Phone className="h-3.5 w-3.5" />{b.phone}</div>
                      </td>
                      <td className="px-6 py-4 text-[#1C1917]">{fmt(b.date)}</td>
                      <td className="px-6 py-4 text-[#1C1917]">{b.time_slot}</td>
                      <td className="px-6 py-4">
                        {b.status === "cancelled" ? (
                          <span className="inline-flex items-center rounded-full bg-[#722F37]/10 text-[#722F37] px-2.5 py-1 text-xs font-semibold">Cancelled</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-[#4A5D23]/10 text-[#4A5D23] px-2.5 py-1 text-xs font-semibold">Confirmed</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-[#1C1917]">{b.guests}</td>
                      <td className="px-6 py-4 text-center text-[#1C1917]">{b.cheeseboards || 0}</td>
                      <td className="px-6 py-4 text-center">
                        {b.wine_club ? (
                          <span className="inline-flex items-center gap-1 text-[#722F37] font-medium">
                            <BadgeCheck className="h-4 w-4" /> Yes
                          </span>
                        ) : (
                          <span className="text-[#A8A29E]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleResend("tasting", b._id)}
                          disabled={resendingId === b._id}
                          data-testid={`resend-booking-${b._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-[#57534E] hover:bg-[#4A5D23]/10 hover:text-[#4A5D23] transition-colors disabled:opacity-50 mr-1"
                          title="Resend confirmation email"
                        >
                          {resendingId === b._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(b._id)}
                          data-testid={`delete-booking-${b._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-[#57534E] hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete booking"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          ) : filteredTables.length === 0 ? (
            <div className="py-20 text-center text-[#57534E]" data-testid="no-table-bookings">No table bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-bookings-table">
                <thead>
                  <tr className="bg-[#FDFBF7] text-left text-[#57534E]">
                    <th className="px-6 py-3 font-semibold">Guest</th>
                    <th className="px-6 py-3 font-semibold">Contact</th>
                    <th className="px-6 py-3 font-semibold">Friday</th>
                    <th className="px-6 py-3 font-semibold">Arrival</th>
                    <th className="px-6 py-3 font-semibold">Status</th>
                    <th className="px-6 py-3 font-semibold text-center">Party</th>
                    <th className="px-6 py-3 font-semibold text-center">Payment</th>
                    <th className="px-6 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTables.map((b) => (
                    <tr key={b._id} className="border-t border-[#E7E5E4] hover:bg-[#FDFBF7]/60 transition-colors" data-testid={`table-row-${b._id}`}>
                      <td className="px-6 py-4 font-medium text-[#1C1917]">{b.name}</td>
                      <td className="px-6 py-4 text-[#57534E]">
                        <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{b.email}</div>
                        <div className="flex items-center gap-1.5 mt-1"><Phone className="h-3.5 w-3.5" />{b.phone}</div>
                      </td>
                      <td className="px-6 py-4 text-[#1C1917]">{fmt(b.date)}</td>
                      <td className="px-6 py-4 text-[#1C1917]">{b.arrival_time}</td>
                      <td className="px-6 py-4">
                        {b.status === "cancelled" ? (
                          <span className="inline-flex items-center rounded-full bg-[#722F37]/10 text-[#722F37] px-2.5 py-1 text-xs font-semibold">Cancelled</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-[#4A5D23]/10 text-[#4A5D23] px-2.5 py-1 text-xs font-semibold">Confirmed</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-[#1C1917]">{b.party_size}</td>
                      <td className="px-6 py-4 text-center text-[#1C1917]">
                        {b.payment_method === "on_arrival" ? (
                          b.amount_due > 0 ? (
                            <span className="inline-flex items-center rounded-full bg-[#722F37]/10 text-[#722F37] px-2.5 py-1 text-xs font-semibold">${b.amount_due.toFixed(0)} on arrival</span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-[#57534E]/10 text-[#57534E] px-2.5 py-1 text-xs font-semibold">On arrival</span>
                          )
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-[#4A5D23]/10 text-[#4A5D23] px-2.5 py-1 text-xs font-semibold">${(b.deposit_paid ?? 50).toFixed(0)} paid</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleResend("table", b._id)}
                          disabled={resendingId === b._id}
                          data-testid={`resend-table-${b._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-[#57534E] hover:bg-[#722F37]/10 hover:text-[#722F37] transition-colors disabled:opacity-50 mr-1"
                          title="Resend confirmation email"
                        >
                          {resendingId === b._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteTable(b._id)}
                          data-testid={`delete-table-${b._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-[#57534E] hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete table booking"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
